"use client";

import { useState } from "react";
import {
  ActionIcon,
  Alert,
  Button,
  Modal,
  NavLink,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { TbMessageDots, TbCircleCheck } from "react-icons/tb";

export interface FeedbackSubmitInput {
  message: string;
  email?: string;
  pagePath: string;
  /** Honeypot — humans never fill this */
  website?: string;
}

export interface FeedbackWidgetLabels {
  trigger?: string;
  title?: string;
  messageLabel?: string;
  messagePlaceholder?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  submit?: string;
  success?: string;
  error?: string;
  close?: string;
}

const DEFAULTS: Required<FeedbackWidgetLabels> = {
  trigger: "Give feedback",
  title: "Help us improve",
  messageLabel: "What can we do better?",
  messagePlaceholder: "Tell us what's confusing, broken, or missing…",
  emailLabel: "Email (optional)",
  emailPlaceholder: "you@example.com",
  submit: "Send feedback",
  success: "Thank you! Your feedback has been sent.",
  error: "Something went wrong. Please try again.",
  close: "Close",
};

export interface FeedbackWidgetProps {
  onSubmit: (input: FeedbackSubmitInput) => Promise<{ success: boolean; error?: string }>;
  labels?: FeedbackWidgetLabels;
  /** "icon" = compact header button with tooltip; "menuItem" = full-width row for mobile drawers */
  variant?: "icon" | "menuItem";
}

const MAX_MESSAGE_LENGTH = 2000;

export function FeedbackWidget({ onSubmit, labels = {}, variant = "icon" }: FeedbackWidgetProps) {
  const t = { ...DEFAULTS, ...labels };
  const [opened, setOpened] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function open() {
    setSubmitted(false);
    setError(null);
    setOpened(true);
  }

  function close() {
    setOpened(false);
    if (submitted) {
      setMessage("");
      setEmail("");
    }
  }

  async function handleSubmit() {
    const trimmed = message.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await onSubmit({
        message: trimmed.slice(0, MAX_MESSAGE_LENGTH),
        email: email.trim() || undefined,
        pagePath: window.location.pathname,
        website: website || undefined,
      });
      if (result.success) {
        setSubmitted(true);
      } else {
        setError(result.error ?? t.error);
      }
    } catch {
      setError(t.error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {variant === "menuItem" ? (
        <NavLink
          label={t.trigger}
          leftSection={<TbMessageDots size={18} />}
          onClick={open}
          variant="light"
        />
      ) : (
        <Tooltip label={t.trigger} withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            aria-label={t.trigger}
            onClick={open}
          >
            <TbMessageDots size={20} />
          </ActionIcon>
        </Tooltip>
      )}

      <Modal opened={opened} onClose={close} title={t.title} size="md" zIndex={210}>
        {submitted ? (
          <Stack gap="md" align="center" py="md">
            <TbCircleCheck size={40} color="var(--mantine-color-green-6)" />
            <Text size="sm" ta="center">{t.success}</Text>
            <Button variant="light" onClick={close}>{t.close}</Button>
          </Stack>
        ) : (
          <Stack gap="md">
            <Textarea
              label={t.messageLabel}
              placeholder={t.messagePlaceholder}
              value={message}
              onChange={(e) => setMessage(e.currentTarget.value)}
              maxLength={MAX_MESSAGE_LENGTH}
              minRows={4}
              autosize
              required
              data-autofocus
            />
            <TextInput
              label={t.emailLabel}
              placeholder={t.emailPlaceholder}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              maxLength={255}
            />
            {/* Honeypot — hidden from humans, bots fill it and get silently dropped */}
            <TextInput
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.currentTarget.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="hidden"
            />
            {error && (
              <Alert color="red" variant="light">{error}</Alert>
            )}
            <Button onClick={handleSubmit} loading={submitting} disabled={!message.trim()} autoContrast>
              {t.submit}
            </Button>
          </Stack>
        )}
      </Modal>
    </>
  );
}
